var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceTechnicalHeadingTypeEnum } from '@inter-ikea-kompis/enums';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import { HyperlinkColorEnum } from '@inter-ikea-kompis/component-hyperlink';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { Text } from '@inter-ikea-kompis/component-text';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import EprelLinkModalEnum from '../../enums/EprelLinkModalEnum.js';
import EnergyFicheCard from '../energy-fiche-card/EnergyFicheCard.js';
import EprelQr from '../eprel-qr/EprelQr.js';
/**
 * Data-driven information listing.
 *
 * @event modalOpen Triggered when opening energy fiche card
 * @event modalClose Triggered when closing energy fiche card
 */
export default class EprelLink extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Visible modal.
        this.visibleModal = null;
        this.sheetElement = null;
    }
    /**
     * @returns Template.
     */
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return this.getLink();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
    }
    async connectedCallback() {
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-hyperlink', Hyperlink);
        this.customElements.define('kompis-eprel-qr', EprelQr);
        this.customElements.define('kompis-energy-fiche-card', EnergyFicheCard);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('visibleModal') && this.hasSheetSupport()) {
            if (this.visibleModal === EprelLinkModalEnum.energyFicheCard) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        if (changedProperties.has('product')) {
            if (!this.hasSheetSupport) {
                this.closeSheet();
            }
        }
        return true;
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation?.translations;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    /**
     * If eprel link has support for opening a fiche card or eprel qr.
     */
    hasSheetSupport() {
        const link = ProductUtility.getProductInformationSheetLink(this.product);
        return (link === null && !!this.getFicheHeader()) || (link !== null && this.disableLinks);
    }
    /**
     * Retrieves eprel link and handles.
     */
    getLink() {
        const link = ProductUtility.getProductInformationSheetLink(this.product);
        if (link === null && !this.getFicheHeader()) {
            return null;
        }
        else if (link === null) {
            // Non-rescaled
            return html `
				<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
					<kompis-hyperlink
						.theme="${this.theme}"
						.color="${HyperlinkColorEnum.grey}"
						.text="${this.getFicheHeader() ?? ''}"
						.underline="${true}"
						@linkClick="${(event) => {
                event.preventDefault();
                this.dispatchEvent(new CustomEvent('modalOpen', {
                    bubbles: true,
                    detail: { visibleModal: EprelLinkModalEnum.energyFicheCard }
                }));
            }}"
					></kompis-hyperlink>
				</kompis-text>
			`;
        }
        else if (this.disableLinks) {
            return html `
				<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
					<kompis-hyperlink
						.theme="${this.theme}"
						.color="${HyperlinkColorEnum.grey}"
						.text="${link.text}"
						.underline="${true}"
						@linkClick="${(event) => {
                event.preventDefault();
                this.dispatchEvent(new CustomEvent('modalOpen', {
                    bubbles: true,
                    detail: { visibleModal: EprelLinkModalEnum.energyFicheCard }
                }));
            }}"
					></kompis-hyperlink>
				</kompis-text>
			`;
        }
        else {
            return html `
				<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
					<kompis-hyperlink
						.target="${'_blank'}"
						.href="${link.href}"
						.theme="${this.theme}"
						.color="${HyperlinkColorEnum.grey}"
						.text="${link.text}"
						.underline="${true}"
					></kompis-hyperlink>
				</kompis-text>
			`;
        }
    }
    /**
     * Return Fiche header if exists.
     */
    getFicheHeader() {
        return (this.product.content?.complianceTechnical?.find((info) => info.headingType === ProductComplianceTechnicalHeadingTypeEnum.nonRescaled)?.headingText ?? null);
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        this.sheetElement = sheetElement;
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const cardElement = this.createCard();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(cardElement);
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        sheetElement.size = 'small';
        sheetElement.fullHeight = true;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.style.zIndex = '500';
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            }
        });
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        return sheetElement;
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const headerSpan = document.createElement('span');
        headerSpan.slot = 'closebutton-label';
        headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(headerSpan);
        return sheetHeaderElement;
    }
    createCard() {
        const link = ProductUtility.getProductInformationSheetLink(this.product);
        if (link === null) {
            const ficheCard = this.createInternalElement('kompis-energy-fiche-card');
            ficheCard.theme = this.theme;
            ficheCard.product = this.product;
            return ficheCard;
        }
        else {
            const eprelQr = this.createInternalElement('kompis-eprel-qr');
            eprelQr.theme = this.theme;
            eprelQr.localizedInformation = this.localizedInformation;
            eprelQr.translations = this.translations;
            eprelQr.product = this.product;
            return eprelQr;
        }
    }
}
__decorate([
    property({ type: Object, required: true })
], EprelLink.prototype, "product", void 0);
__decorate([
    property({ type: Object, required: true })
], EprelLink.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], EprelLink.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], EprelLink.prototype, "translations", void 0);
__decorate([
    property({ type: Boolean })
], EprelLink.prototype, "disableLinks", void 0);
__decorate([
    property({ type: String })
], EprelLink.prototype, "visibleModal", void 0);
//# sourceMappingURL=EprelLink.js.map